import type { ReactNode } from "react";
import { Button as MantineButton, type ButtonProps as MantineButtonProps } from "@mantine/core";

/*
 * The one button — backed by Mantine's `Button`. The public API
 * (variant / size / block / loading / icon) is preserved so every call site
 * across the app uses one vocabulary. `variant`/`size` map onto Mantine's
 * variant + size scale.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

// Mantine's own `Button` props minus the ones we re-derive from our own vocab.
type MantinePassthrough = Omit<
  MantineButtonProps,
  "variant" | "size" | "color" | "fullWidth" | "loading" | "leftSection"
>;

export interface ButtonProps
  extends MantinePassthrough,
    Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      keyof MantinePassthrough | "color"
    > {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to fill the available inline width. */
  block?: boolean;
  /** Render a spinner and disable interaction. */
  loading?: boolean;
  /** Optional leading glyph/icon. */
  icon?: ReactNode;
}

/** Map our intent vocab → Mantine (variant, color). */
function mantineVariant(variant: ButtonVariant): { variant: string; color?: string } {
  switch (variant) {
    case "primary":
      return { variant: "filled" };
    case "secondary":
      return { variant: "default" };
    case "ghost":
      return { variant: "subtle", color: "gray" };
    case "danger":
      return { variant: "filled", color: "red" };
  }
}

const SIZE_MAP: Record<ButtonSize, string> = {
  sm: "xs",
  md: "sm",
  lg: "md",
};

export function Button({
  variant = "secondary",
  size = "md",
  block = false,
  loading = false,
  icon,
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  const v = mantineVariant(variant);
  return (
    <MantineButton
      {...(rest as MantineButtonProps)}
      type={type}
      variant={v.variant}
      color={v.color}
      size={SIZE_MAP[size]}
      fullWidth={block}
      loading={loading}
      leftSection={icon ?? undefined}
    >
      {children}
    </MantineButton>
  );
}
